import { useRef, useState } from 'react';
import Card from '../../components/card/Card';
import Button from '../../components/button/Button';
import Modal from '../../components/modal/modal';
import P from '../../components/texts/p/P';
import { useToast } from '../../components/toast/toast';
import {
  applyRedefinedInstallments,
  downloadInstallmentsTemplate,
  previewRedefinedInstallments,
  InstallmentRedefinitionPreviewResult,
} from '../../services/GuaranteesService';

interface InstallmentRedefinitionActionsProps {
  loanId: string | number;
  /** monetization_status_type_id: 1 = "Pendiente de ejecución" (only visible state) */
  monetizationStatusTypeId?: number | null;
  /** "loan" — checks/promissory notes/commercial endorsements do NOT show these buttons */
  guaranteeType?: string;
  /** Guarantee label for the downloaded file name */
  guaranteeNumber?: string | number;
  /** Callback to refresh the grid after a successful redefinition */
  onSuccess: () => void;
  /** Toast container id (default 'overdue-toast' to reuse from InstallmentPlanTab) */
  toastId?: string;
  /** Horizontal alignment of the action buttons (default end for detail tab) */
  justifyContent?: 'flex-start' | 'flex-end';
  /** Top margin; use false when embedded next to another control */
  withTopMargin?: boolean;
  /**
   * toolbar — detail tab above the grid (default).
   * inlineForm — Ejecución de aval: size/dimensions aligned with sibling DatePicker.
   */
  layout?: 'toolbar' | 'inlineForm';
}

/** Matches LoanExecutionSection DatePicker (BaseInput ≈ padding + 34px tag ≈ 50px; column ~240px). */
const INLINE_FORM_BUTTON_STYLE = {
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'center',
  minWidth: 240,
  width: 240,
  minHeight: 50,
  height: 50,
  padding: '0 16px',
  boxSizing: 'border-box' as const,
  textAlign: 'center' as const,
};

const PENDING_EXECUTION_STATUS_ID = 1;

const formatNumber = (n: number, isAsset = false) =>
  new Intl.NumberFormat('es-AR', {
    minimumFractionDigits: isAsset ? 4 : 2,
    maximumFractionDigits: isAsset ? 4 : 2,
  }).format(n);

const InstallmentRedefinitionActions: React.FC<InstallmentRedefinitionActionsProps> = ({
  loanId,
  monetizationStatusTypeId,
  guaranteeType,
  guaranteeNumber,
  onSuccess,
  toastId = 'overdue-toast',
  justifyContent = 'flex-end',
  withTopMargin = true,
  layout = 'toolbar',
}) => {
  const fileInputRef = useRef<HTMLInputElement | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [isDownloading, setIsDownloading] = useState(false);
  const [isApplying, setIsApplying] = useState(false);
  const [previewData, setPreviewData] = useState<InstallmentRedefinitionPreviewResult | null>(null);
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [errorMessages, setErrorMessages] = useState<string[]>([]);
  const [modalOpen, setModalOpen] = useState(false);

  // Conditional visibility: only for loan-type guarantees in "Pending Execution" status (RN-OPE-01/02)
  const isLoan = (guaranteeType ?? '').toLowerCase() === 'loan';
  const isPendingExecution = monetizationStatusTypeId === PENDING_EXECUTION_STATUS_ID;
  if (!isLoan || !isPendingExecution) return null;

  const handleDownload = async () => {
    setIsDownloading(true);
    setErrorMessages([]);
    try {
      const response: any = await downloadInstallmentsTemplate(loanId);
      if (!response?.success || !response.data) {
        useToast.error({
          id: toastId,
          title: 'No se pudo descargar la plantilla',
          description: response?.message ?? 'Intentá nuevamente.',
        });
        return;
      }
      const blob = new Blob([response.data], {
        type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
      });
      const link = document.createElement('a');
      const url = URL.createObjectURL(blob);
      link.href = url;
      link.download = `plantilla-cuotas-aval-${guaranteeNumber ?? loanId}.xlsx`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
    } catch (err) {
      console.error('Error downloading template:', err);
      useToast.error({
        id: toastId,
        title: 'Error inesperado',
        description: 'No se pudo descargar la plantilla.',
      });
    } finally {
      setIsDownloading(false);
    }
  };

  const handleSelectFileClick = () => fileInputRef.current?.click();

  const extractErrorMessages = (response: any): string[] => {
    // 1. Prioritize errors[] if it comes as an array of strings
    if (Array.isArray(response?.errors) && response.errors.length > 0) {
      return response.errors.map((e: any) => (typeof e === 'string' ? e : JSON.stringify(e)));
    }
    // 2. If message is a string, use it
    if (typeof response?.message === 'string' && response.message) {
      return [response.message];
    }
    // 3. If message is an object, try to extract useful info
    if (response?.message && typeof response.message === 'object') {
      const msg = response.message;
      if (Array.isArray(msg)) return msg.map((m: any) => String(m));
      if (msg.message) return [String(msg.message)];
      return [JSON.stringify(msg)];
    }
    return [];
  };

  const normalizePreviewResponse = (raw: any): InstallmentRedefinitionPreviewResult => ({
    currentInstallments: raw.currentInstallments ?? raw.current_installments ?? 0,
    newInstallments: raw.newInstallments ?? raw.new_installments ?? 0,
    amortizationSum: raw.amortizationSum ?? raw.amortization_sum ?? 0,
    monetizationAmount:
      raw.comparisonTerm ?? raw.comparison_term ?? raw.monetizationAmount ?? raw.monetization_amount ?? 0,
    comparisonTerm: raw.comparisonTerm ?? raw.comparison_term ?? raw.monetizationAmount ?? raw.monetization_amount ?? 0,
    comparisonUnit: raw.comparisonUnit ?? raw.comparison_unit,
    isAsset: raw.isAsset ?? raw.is_asset ?? false,
    difference: raw.difference ?? 0,
    tolerance: raw.tolerance ?? 0,
    warnings: raw.warnings ?? [],
  });

  const handleFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    e.target.value = ''; // allows re-selecting the same file
    if (!file) return;
    setSelectedFile(file);
    setErrorMessages([]);
    setIsLoading(true);
    try {
      const response: any = await previewRedefinedInstallments(loanId, file);
      if (response?.success && response.data) {
        setPreviewData(normalizePreviewResponse(response.data));
        setModalOpen(true);
      } else {
        const apiErrors = extractErrorMessages(response);
        setErrorMessages(apiErrors.length > 0 ? apiErrors : ['No se pudo validar el archivo. Verificá el formato.']);
        setPreviewData(null);
        setModalOpen(false);
      }
    } catch (err) {
      console.error('Error in preview:', err);
      setErrorMessages(['Error inesperado al validar el archivo.']);
    } finally {
      setIsLoading(false);
    }
  };

  const handleConfirm = async () => {
    if (!selectedFile) return;
    setIsApplying(true);
    try {
      const response: any = await applyRedefinedInstallments(loanId, selectedFile);
      if (response?.success) {
        useToast.success({
          id: toastId,
          title: 'Plan de cuotas actualizado correctamente',
        });
        setModalOpen(false);
        setPreviewData(null);
        setSelectedFile(null);
        onSuccess();
      } else {
        const apiErrors = extractErrorMessages(response);
        setErrorMessages(apiErrors.length > 0 ? apiErrors : ['No se pudo aplicar la redefinición.']);
        setModalOpen(false);
      }
    } catch (err) {
      console.error('Error in apply:', err);
      setErrorMessages(['Error inesperado al aplicar la redefinición.']);
      setModalOpen(false);
    } finally {
      setIsApplying(false);
    }
  };

  const handleCancel = () => {
    setModalOpen(false);
    setPreviewData(null);
    setSelectedFile(null);
  };

  const isInlineForm = layout === 'inlineForm';
  const buttonSize = isInlineForm ? 'lg' : 'md';
  const buttonStyle = isInlineForm ? INLINE_FORM_BUTTON_STYLE : undefined;
  const buttonIconAlign = isInlineForm ? 'center' : 'none';

  return (
    <Card
      display='flex'
      gap='12px'
      margin={{ top: withTopMargin ? '4' : '0', bottom: '2' }}
      justifyContent={justifyContent}
      alignItems='center'
      flexWrap='wrap'
    >
      <Button
        variant='secondary'
        size={buttonSize}
        iconAlign={buttonIconAlign}
        style={buttonStyle}
        onClick={handleDownload}
        disabled={isDownloading || isLoading || isApplying}
        id='btn-download-installments-template'
        testId='btn-download-installments-template'
      >
        {isDownloading ? 'Descargando...' : 'Descargar plantilla'}
      </Button>
      <Button
        variant='primary'
        size={buttonSize}
        iconAlign={buttonIconAlign}
        style={buttonStyle}
        onClick={handleSelectFileClick}
        disabled={isLoading || isApplying || isDownloading}
        id='btn-upload-redefined-installments'
        testId='btn-upload-redefined-installments'
      >
        {isLoading ? 'Validando...' : 'Subir cuotas redefinidas'}
      </Button>
      <input
        ref={fileInputRef}
        type='file'
        accept='.xlsx'
        style={{ display: 'none' }}
        onChange={handleFileChange}
        data-testid='input-file-installments'
      />

      {errorMessages.length > 0 && (
        <Card
          width='100%'
          margin={{ top: '2' }}
          padding='12px'
          style={{
            background: '#FEE2E2',
            border: '1px solid #DC2626',
            borderRadius: 8,
          }}
          data-testid='panel-redefinition-errors'
        >
          <P weight='bold' withMargin={false} style={{ color: '#991B1B' }}>
            El archivo tiene errores que deben corregirse:
          </P>
          <ul style={{ margin: '8px 0 0 16px', color: '#991B1B' }}>
            {errorMessages.map((msg, i) => (
              <li key={i}>{msg}</li>
            ))}
          </ul>
        </Card>
      )}

      <Modal
        isOpen={modalOpen}
        onClose={handleCancel}
        title='¿Está seguro de aplicar los cambios?'
        secondaryText='Cancelar'
        secondaryVariant='secondary'
        onSecondaryAction={handleCancel}
        primaryText='Confirmar'
        primaryVariant='primary'
        onPrimaryAction={handleConfirm}
        loadingPrimaryBtn={isApplying}
        primaryActionDisabled={isApplying || errorMessages.length > 0}
        width='520px'
      >
        {previewData && (
          <Card display='flex' style={{ flexDirection: 'column' }} gap='8px'>
            <P withMargin={false}>
              <strong>Cuotas actuales:</strong> {previewData.currentInstallments} —{' '}
              <strong>Cuotas nuevas:</strong> {previewData.newInstallments}
            </P>
            <P withMargin={false}>
              <strong>Suma amortización (archivo):</strong>{' '}
              {formatNumber(previewData.amortizationSum, previewData.isAsset)}
            </P>
            <P withMargin={false}>
              <strong>{previewData.isAsset ? 'Cantidad del activo:' : 'Monto monetizado:'}</strong>{' '}
              {formatNumber(previewData.monetizationAmount, previewData.isAsset)}
            </P>
            <P withMargin={false}>
              <strong>Diferencia:</strong>{' '}
              {previewData.difference >= 0 ? '+' : ''}
              {formatNumber(previewData.difference, previewData.isAsset)} (dentro de tolerancia ±
              {formatNumber(previewData.tolerance, previewData.isAsset)})
            </P>

            {previewData.warnings.length > 0 && (
              <Card
                margin={{ top: '2' }}
                padding='8px'
                style={{
                  background: '#FEF3C7',
                  border: '1px solid #F59E0B',
                  borderRadius: 8,
                }}
                data-testid='panel-redefinition-warnings'
              >
                <P weight='bold' withMargin={false} style={{ color: '#92400E' }}>
                  Advertencias (no bloqueantes):
                </P>
                <ul style={{ margin: '6px 0 0 16px', color: '#92400E' }}>
                  {previewData.warnings.map((w, i) => (
                    <li key={i}>{w}</li>
                  ))}
                </ul>
              </Card>
            )}

            <P withMargin={false} style={{ marginTop: 8, fontStyle: 'italic' }}>
              Esta acción no se puede deshacer.
            </P>
          </Card>
        )}
      </Modal>
    </Card>
  );
};

export default InstallmentRedefinitionActions;
